import { useParams, Navigate } from 'react-router-dom'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import { useTranslation } from 'react-i18next'
import { useShop } from '../shared/api/storefront'
import type { Shop } from '../shared/types'

/**
 * The shop's policy pages (terms, privacy, refunds, shipping) — required by payment gateways,
 * which audit them by clicking the footer links on the live site before enabling API keys.
 *
 * Shop identity (name, phone, address) comes from the tenant record, so every shop gets its own.
 * The delivery terms below are the only shop-specific text in code.
 * ponytail: constants, not Tenant columns — move them (plus a settings UI) when shop #2 needs
 * different delivery terms. Until then a schema change buys nothing.
 */
const DELIVERY_RADIUS_KM = 5
const DELIVERY_HOURS = '8:00 AM to 8:00 PM'
const LAST_UPDATED = '01-Aug-2026'

/** Not translated: these are legal documents in English, like the shop's own copy (§10). */
type Section = { heading: string; paras?: string[]; bullets?: string[] }
type LegalDoc = { title: string; intro: string; sections: Section[] }

function buildDocs(shop: Shop): Record<string, LegalDoc> {
  const name = shop.name
  const phone = shop.phone ?? ''
  const address = shop.address ?? ''

  return {
    terms: {
      title: 'Terms & Conditions',
      intro: `Welcome to ${name}. By accessing our website and placing an order, you agree to be bound by the following terms and conditions:`,
      sections: [
        {
          heading: 'General',
          paras: [
            `This website is operated by ${name}. We offer fresh meat and poultry products for local delivery. By visiting our site or purchasing something from us, you engage in our "Service" and agree to the terms outlined here.`,
          ],
        },
        {
          heading: 'Product Accuracy and Availability',
          paras: [
            'We strive to ensure that all details, prices, and product descriptions are accurate. However, as meat is naturally cut and prepared, slight variations in exact weight may occur. All orders are subject to availability.',
          ],
        },
        {
          heading: 'Pricing and Payments',
          paras: [
            'All prices are listed in Indian Rupees (INR) and are inclusive of applicable taxes. We accept online payments via major credit/debit cards, UPI, and net banking through our secure payment gateway.',
          ],
        },
        {
          heading: 'Food Safety',
          paras: ['We operate under strict hygiene standards compliant with FSSAI regulations.'],
        },
        {
          heading: 'Contact Information',
          paras: [
            'If you have any questions about these Terms, please contact us at:',
            `Phone: ${phone}`,
            `Address: ${address}`,
          ],
        },
      ],
    },

    privacy: {
      title: 'Privacy Policy',
      intro: `${name} ("we", "us", or "our") respects your privacy and is committed to protecting your personal data. This policy explains how we collect and use your information when you visit our website or make a purchase.`,
      sections: [
        {
          heading: 'Information We Collect',
          paras: [
            'When you place an order, we collect your name, billing address, delivery address, phone number, and email address to fulfill your order.',
          ],
        },
        {
          heading: 'How We Use Your Information',
          paras: ['We use your information strictly to:'],
          bullets: [
            'Process and deliver your orders.',
            'Communicate with you regarding order updates or issues.',
            'Prevent fraudulent transactions.',
          ],
        },
        {
          heading: 'Payment Information',
          paras: [
            'We do not store your credit card or UPI details on our servers. All payment transactions are processed securely through our verified third-party payment gateway provider.',
          ],
        },
        {
          heading: 'Data Sharing',
          paras: [
            'We do not sell, trade, or rent your personal identification information to others. We only share necessary details (like your address and phone number) with our delivery personnel to ensure your order reaches you.',
          ],
        },
        {
          heading: 'Contact Us',
          paras: [`For any concerns regarding your privacy, contact us at ${phone}.`],
        },
      ],
    },

    refunds: {
      title: 'Refund & Cancellation Policy',
      intro:
        'Due to the highly perishable nature of fresh meat and poultry, and to maintain the highest standards of food safety and hygiene, we enforce a strict refund and cancellation policy.',
      sections: [
        {
          heading: 'No Cancellations',
          paras: [
            'Because our products are cut and prepared fresh immediately upon receiving your order, once an order is successfully placed and confirmed on the website, it cannot be cancelled or modified. Please review your cart carefully before completing your payment.',
          ],
        },
        {
          heading: 'No Refunds or Returns',
          paras: [
            'All sales are final. We do not accept returns or offer refunds for any products once they have been delivered.',
          ],
        },
        {
          heading: 'Exceptions',
          paras: [
            `In the rare event that the wrong item was delivered to you due to an error on our part, please contact us immediately at ${phone} upon receiving the delivery, and we will arrange to replace the incorrect item.`,
          ],
        },
      ],
    },

    shipping: {
      title: 'Shipping & Delivery Policy',
      intro:
        'We strive to deliver fresh, high-quality products directly to your doorstep. Please review our delivery parameters below:',
      sections: [
        {
          heading: 'Delivery Area',
          paras: [
            `We exclusively serve our local community. Delivery is currently restricted to a ${DELIVERY_RADIUS_KM}-kilometre radius from our shop location${address ? ` at ${address}` : ''}. Orders placed for addresses outside this ${DELIVERY_RADIUS_KM} km radius will not be processed.`,
          ],
        },
        {
          heading: 'Delivery Timing',
          bullets: [
            `Our delivery hours are from ${DELIVERY_HOURS}, seven days a week.`,
            'Orders placed will be dispatched and delivered within our operating hours. Orders placed after 8:00 PM will be processed the following morning.',
          ],
        },
        {
          heading: 'Delivery Charges',
          paras: [
            `We are pleased to offer free delivery on all orders within our ${DELIVERY_RADIUS_KM} km service radius. There are no hidden shipping or handling fees.`,
          ],
        },
        {
          heading: 'Receiving Your Order',
          paras: [
            'Because our products are highly perishable, it is mandatory that someone is available at the provided delivery address to receive the order. If our delivery personnel cannot reach you at the address or via phone, the order will be forfeited, and no refunds will be issued.',
          ],
        },
      ],
    },
  }
}

/** The four policy documents, one per `/legal/:doc` URL. Unknown slugs fall back to the shop. */
export default function LegalPage() {
  const { doc } = useParams()
  const { t } = useTranslation()
  const { data: shop, isLoading } = useShop()

  if (isLoading || !shop) {
    return (
      <Container maxWidth="md" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress aria-label={t('common.loading')} />
      </Container>
    )
  }

  const docs = buildDocs(shop)
  const content = doc ? docs[doc] : undefined
  if (!content) return <Navigate to="/" replace />

  return (
    <Container maxWidth="md" sx={{ py: 4, pb: 8 }}>
      <Typography variant="h5" component="h1" fontWeight={700}>
        {content.title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Last updated: {LAST_UPDATED}
      </Typography>

      <Typography variant="body1" sx={{ mt: 2 }}>
        {content.intro}
      </Typography>

      {content.sections.map((section, index) => (
        <Box key={section.heading} sx={{ mt: 3 }}>
          <Typography variant="subtitle1" component="h2" fontWeight={700} sx={{ mb: 0.5 }}>
            {index + 1}. {section.heading}
          </Typography>
          {section.paras?.map((para) => (
            <Typography key={para} variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {para}
            </Typography>
          ))}
          {section.bullets && (
            <Box component="ul" sx={{ pl: 3, my: 0 }}>
              {section.bullets.map((bullet) => (
                <Typography key={bullet} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {bullet}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </Container>
  )
}
